import React, { useEffect, useRef, useState } from 'react';
import { useModal } from '@ampol/design-system';
import { globalSearchRequest, globalSearchReset } from 'actions/cardActions';
import Alert from 'constants/alert-messages';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackCustomEvent } from 'utilities/app-insights';

export default function useGlobalSearchController() {
  const dispatch = useDispatch();

  // React Router v6 APIs
  const navigate = useNavigate();
  const location = useLocation();        

  // Redux selectors
  const isLoading   = useSelector((state) => state.cardsStore.globalSearchLoading);
  const isError     = useSelector((state) => state.cardsStore.globalSearchError);
  const isLoaded    = useSelector((state) => state.cardsStore.globalSearchLoaded);
  const resultValue = useSelector((state) => state.cardsStore.globalSearchResult);
  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);

  const cashOrderCardDetails     = useSelector((state) => state.cashStore.cashOrderCardDetails);
  const cashOrdersListResultSize = useSelector((state) => state.cashStore.cashOrdersListResultSize);
  const isCashLoaded             = useSelector((state) => state.cashStore.cashOrdersListLoaded);

  // Local state / refs
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  // Error modal (Ampol design system)
  const errorModal = useModal({
    id: 'error-modal',
    title: isError && 'Card not found',
    content: <div>{isError && Alert.GLOBAL_SEARCH_NOT_FOUND}</div>,
    variant: 'single',
    color: 'blue',
    actions: [
      { label: 'OK', onClick: () => errorModal.close() },
    ],
    onClose: () => {
      dispatch(globalSearchReset());
      handleClear();
    },
  });

  // Handle modal / blur behavior based on search state
  useEffect(() => {
    if (isError || (isLoaded && !resultValue)) {
      errorModal.open();
    } else if (isLoaded && resultValue) {
      inputRef.current?.blur();
    } else if (!isLoaded && resultValue) {
      handleClear();
    }
  }, [isError, isLoaded, resultValue]);

  // Clear global search when navigating away from /cards
  // v6: useLocation replaces history.listen
  useEffect(() => {
    const isGlobalSearchNav = location.state === 'globalSearch';
    const isOnCardsRoute = location.pathname.startsWith('/cards');

    // If this navigation wasn't initiated by global search and we have a result,
    // clear when we're not on /cards.
    if (!isGlobalSearchNav && resultValue && !isOnCardsRoute) {
      handleClear();
    }
  }, [location.pathname, location.state, resultValue]);

  // Clear global search if user performs cash list search with different filters
  useEffect(() => {
    if (
      isLoaded &&
      isCashLoaded &&
      (cashOrdersListResultSize !== 1 ||
        cashOrderCardDetails?.cardNumber !== resultValue)
    ) {
      handleClear();
    }
  }, [isCashLoaded]);

  // Actions
  const handleClear = () => {
    dispatch(globalSearchReset());
    setInputValue('');
  };

  const handleClearClick = () => {
    handleClear();
  };

  const handleSubmit = (searchInput) => {
    // Pass navigate to the action (see patch below) if it needs to change routes
    dispatch(globalSearchRequest(searchInput, navigate));

    trackCustomEvent(
      'Submit Global Search',
      loggedInUser,
      undefined,
      'Entered text to search for a card or registration across all assigned accounts.'
    );
  };

  const handleChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSubmit(event.target.value);
    }
  };

  return {
    inputValue,
    isLoading,
    inputRef,
    handleChange,
    handleSubmit,
    handleKeyDown,
    handleClearClick,
  };
}
